// Boiler Plate Imports
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";
import colors from "../../theme/colors";
import { FullPageFormWrapper } from "../../components/Forms/shared";

// Conatiner Component Imports
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import ModalComponent from "../../components/Modal";

// Page Component Imports
import AdminNetworkForm from "../../components/Forms/adminNetworkForm";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmForm from "../../components/Forms/confirmForm";
import { CircularProgress } from "@mui/material";
import AdminUserForm from "../../components/Forms/adminUserForm";
import { update as updateUserProfile } from "../../redux-store/userSlice";

const EditUser = () => {
  const params = useParams();
  const { userId } = params;

  const userProfile = useSelector((state) => state.user);
  const [loadingData, setLoadingData] = useState(true);

  const [organizationsList, setOrganizationsList] = useState([]);
  const [networksList, setNetworksList] = useState([]);

  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState(false);
  const [user, setUser] = useState({});

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openReactivateModal, setOpenReactivateModal] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        dataLayer.GetAllOrganizations(),
        dataLayer.GetAllNetworks(),
        dataLayer.GetUser(),
      ]);
      setLoadingData(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: "Admin Users",
          link: "/admin/users",
        },
        {
          title: "Edit User",
          link: `/admin/users/${userId}/edit`,
        },
      ])
    );
  }, []);

  const dataLayer = {
    GetAllOrganizations: async () => {
      const { organizations } = await API.Organization.OrganizationsGetAll();
      setOrganizationsList(organizations);
    },
    GetAllNetworks: async () => {
      const { networks } = await API.Network.NetworkGetAll();
      setNetworksList(networks);
    },
    CreateUser: async ({ createUserData }) => {
      return await API.User.UserCreate({ createUserData });
    },
    GetUser: async () => {
      const _user = await API.User.UserGet({ userId });
      setUser(_user);
    },
    GetUserProfile: async () => {
      const userProfile = await API.User.UserGet();
      dispatch(updateUserProfile(userProfile));
    },
  };

  const handlers = {
    confirmModalSubmit: () => {
      navigate(`/admin/users`);
    },
    confirmModalCancel: () => {
      setShowConfirmModal(false);
    },
    cancel: () => {
      setShowConfirmModal(true);
    },
    saveEditUser: async (data) => {
      setSavingError("");
      setSaving(true);
      try {
        await API.User.UserUpdate({
          editUserData: { id: userId, ...data },
        });
        //Did I just update me?
        if (userProfile.id === user.id) {
          await dataLayer.GetUserProfile();
        }

        //Create Success Notification
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User updated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);

        navigate(`/admin/users`);
      } catch (err) {
        setSavingError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSaving(false);
    },
    confirmDeactivateUserModal: () => {
      setOpenDeactivateModal(true);
    },
    deactivateUser: async () => {
      setSavingError("");
      setSaving(true);
      try {
        await API.User.UserDelete({
          deleteUserId: user.id,
        });

        //Reload Data
        dataLayer.GetUser();

        //Create Success Notification
        setOpenDeactivateModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User deactivated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSaving(false);
    },
    confirmReactivateUserModal: () => {
      setOpenReactivateModal(true);
    },
    reactivateUser: async () => {
      setSavingError("");
      setSaving(true);
      try {
        await API.User.UserDelete({
          deleteUserId: user.id,
          reactivate: "true",
        });

        //Reload Data
        dataLayer.GetUser();

        setOpenReactivateModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User reactivated successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSaving(false);
    },
    resendUserInviteEmail: async () => {
      setSavingError("");
      setSaving(true);
      try {
        await API.User.ResendUserInvite({
          resendUserId: user.id,
        });

        setOpenReactivateModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "User invite resent. Check email.",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSaving(false);
    },
    closeDeactivateModal: () => {
      setOpenDeactivateModal(false);
    },
    closeReactivateModal: () => {
      setOpenReactivateModal(false);
    },
  };

  return (
    <>
      
      <FullPageFormWrapper>
        <h2>
          Edit User <span style={{ color: colors.gray }}> / Boreas Admin</span>
        </h2>
        {!loadingData ? (
          <AdminUserForm
            user={user}
            handleSave={handlers.saveEditUser}
            handleCancel={handlers.cancel}
            loading={saving}
            error={savingError}
            isEdit={true}
            showDeactivate={user.id !== userProfile.id}
            handleDeactivateUser={handlers.confirmDeactivateUserModal}
            handleReactivateUser={handlers.confirmReactivateUserModal}
            handleResendInvite={handlers.resendUserInviteEmail}
            organizationsList={organizationsList}
            networksList={networksList}
          />
        ) : (
          <CircularProgress />
        )}
      </FullPageFormWrapper>
      <ModalComponent
        open={showConfirmModal}
        handleClose={handlers.confirmModalCancel}
        headerText={"Cancel Editing User"}
      >
        <ConfirmForm
          handleSave={handlers.confirmModalSubmit}
          handleCancel={handlers.confirmModalCancel}
          loading={false}
        >
          <div>
            <b>Are you sure you want to Cancel Editing User?</b>
            <p>You will lose all progress.</p>
          </div>
        </ConfirmForm>
      </ModalComponent>
      <ModalComponent
        headerText={`Reactivate ${user.firstName} ${user.lastName}`}
        open={openReactivateModal}
      >
        <ConfirmForm
          handleSave={handlers.reactivateUser}
          handleCancel={handlers.closeReactivateModal}
          loading={saving}
          error={savingError}
        >
          <div>
            <b>
              Are you sure you want to Reactivate {user.firstName}{" "}
              {user.lastName}?
            </b>
            <p>
              This will enable this user's access to Boreas. They will be able
              to sign in. You can revoke access by deactivating this user.
            </p>
          </div>
        </ConfirmForm>
      </ModalComponent>
      <ModalComponent
        headerText={`Deactivate ${user.firstName} ${user.lastName}`}
        open={openDeactivateModal}
      >
        <ConfirmForm
          handleSave={handlers.deactivateUser}
          handleCancel={handlers.closeDeactivateModal}
          loading={saving}
          error={savingError}
        >
          <div>
            <b>
              Are you sure you want to Deactivate {user.firstName}{" "}
              {user.lastName}?
            </b>
            <p>
              This will disable this user's access to Boreas. They will be
              unable to sign in. The user will not be deleted. You can restore
              access by reactivating this user.
            </p>
          </div>
        </ConfirmForm>
      </ModalComponent>
    </>
  );
};

export default EditUser;
